'use client';

import { useEffect } from 'react';

/**
 * Normalize the browser away from showing native `title` tooltips.
 *
 * Native tooltips can't be styled or suppressed via CSS — only by removing
 * the `title` attribute from the DOM. This component sweeps every element
 * with a `title` on mount, preserves the value as `data-title` (so any
 * future custom tooltip / a11y tooling can still read it), then strips
 * the attribute. A MutationObserver catches anything React renders later.
 */
export default function DisableTitleTooltips() {
  useEffect(() => {
    const stripTitle = (el: Element) => {
      const value = el.getAttribute('title');
      if (value === null) return;
      // Preserve the original copy on data-title for any consumer that
      // wants to render a custom tooltip later.
      el.setAttribute('data-title', value);
      el.removeAttribute('title');
    };

    document.querySelectorAll('[title]').forEach(stripTitle);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'title') {
          if (m.target instanceof Element) stripTitle(m.target);
          continue;
        }
        if (m.type === 'childList') {
          m.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.hasAttribute('title')) stripTitle(node);
            node.querySelectorAll('[title]').forEach(stripTitle);
          });
        }
      }
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['title'],
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
